import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { 
  FileText, Sparkles, Download, Save, Plus, Trash2, ArrowLeft, 
  Briefcase, GraduationCap, Award, Lock, CheckCircle 
} from 'lucide-react';
import { api } from '../App';
import { toast } from 'sonner';
import Footer from '../components/Footer';

const TEMPLATE_COLORS = {
  professional: 'bg-blue-500',
  creative: 'bg-purple-500',
  executive: 'bg-slate-700',
  tech: 'bg-teal-500',
  minimalist: 'bg-gray-400'
};

export default function ResumeBuilder({ user }) {
  const [hasAccess, setHasAccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState({});
  const [selectedTemplate, setSelectedTemplate] = useState('professional');
  const [showAISuggestions, setShowAISuggestions] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [aiSection, setAiSection] = useState('summary');
  const navigate = useNavigate();

  const [resume, setResume] = useState({
    full_name: user?.full_name || '',
    email: user?.email || '',
    phone: '',
    linkedin: '',
    summary: '',
    experience: [],
    education: [],
    skills: [],
    template: 'professional'
  });

  useEffect(() => {
    checkAccess();
  }, []);

  const checkAccess = async () => {
    try {
      const res = await api.get('/purchases/check/resume_builder');
      setHasAccess(res.data.has_access);
      if (res.data.has_access) {
        loadTemplates();
        loadResume();
      }
    } catch (error) {
      console.error('Access check failed');
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = async () => {
    try {
      const res = await api.get('/resume-builder/templates');
      setTemplates(res.data.templates);
    } catch (error) {
      console.error('Failed to load templates');
    }
  };

  const loadResume = async () => {
    try {
      const res = await api.get('/resume-builder/my-resume');
      if (res.data && Object.keys(res.data).length > 0) {
        setResume(prev => ({ ...prev, ...res.data }));
        setSelectedTemplate(res.data.template || 'professional');
      }
    } catch (error) {
      console.error('Failed to load resume');
    }
  };

  const saveResume = async () => {
    try {
      await api.post('/resume-builder/save', { ...resume, template: selectedTemplate });
      toast.success('Resume saved successfully!');
    } catch (error) {
      toast.error('Failed to save resume');
    }
  };

  const getAISuggestions = async (section) => {
    setAiSection(section);
    try {
      const res = await api.post('/resume-builder/ai-suggest', {
        section,
        current_content: resume[section] || '',
        job_title: '',
        industry: ''
      });
      setAiSuggestions(res.data);
      setShowAISuggestions(true);
    } catch (error) {
      toast.error('Failed to get AI suggestions');
    }
  };

  const applySuggestion = (suggestion) => {
    if (aiSection === 'skills') {
      setResume(prev => ({
        ...prev,
        skills: [...new Set([...prev.skills, suggestion])]
      }));
    } else {
      setResume(prev => ({
        ...prev,
        [aiSection]: prev[aiSection] ? `${prev[aiSection]}\n${suggestion}` : suggestion
      }));
    }
    toast.success('Suggestion applied!');
  };

  const addExperience = () => {
    setResume(prev => ({
      ...prev,
      experience: [...prev.experience, { company: '', title: '', dates: '', description: '' }]
    }));
  };

  const updateExperience = (index, field, value) => {
    setResume(prev => ({
      ...prev,
      experience: prev.experience.map((exp, i) => i === index ? { ...exp, [field]: value } : exp)
    }));
  };

  const removeExperience = (index) => {
    setResume(prev => ({
      ...prev,
      experience: prev.experience.filter((_, i) => i !== index)
    }));
  };

  const addEducation = () => {
    setResume(prev => ({
      ...prev,
      education: [...prev.education, { school: '', degree: '', dates: '' }]
    }));
  };

  const updateEducation = (index, field, value) => {
    setResume(prev => ({
      ...prev,
      education: prev.education.map((edu, i) => i === index ? { ...edu, [field]: value } : edu)
    }));
  };

  const removeEducation = (index) => {
    setResume(prev => ({
      ...prev,
      education: prev.education.filter((_, i) => i !== index)
    }));
  };

  const addSkill = (skill) => {
    if (skill && !resume.skills.includes(skill)) {
      setResume(prev => ({ ...prev, skills: [...prev.skills, skill] }));
    }
  };

  const removeSkill = (skill) => {
    setResume(prev => ({ ...prev, skills: prev.skills.filter(s => s !== skill) }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center">
          <CardHeader>
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-blue-600" />
            </div>
            <CardTitle>Resume Builder</CardTitle>
            <CardDescription>Purchase Resume Builder to access professional templates and AI-powered suggestions</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold mb-4">$29</p>
            <Button onClick={() => navigate('/marketplace')} className="w-full" data-testid="go-to-marketplace">
              Purchase Now
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-btn">
              <ArrowLeft size={20} />
            </Button>
            <div className="flex items-center gap-3">
              <FileText className="w-8 h-8 text-blue-600" />
              <h1 className="text-2xl font-heading font-bold">Resume Builder</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={saveResume} data-testid="save-resume-btn">
              <Save size={16} className="mr-2" /> Save
            </Button>
            <Button data-testid="download-resume-btn">
              <Download size={16} className="mr-2" /> Download PDF
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Editor Panel */}
          <div className="lg:col-span-2 space-y-6">
            <Tabs defaultValue="personal" className="w-full">
              <TabsList className="grid grid-cols-4 w-full">
                <TabsTrigger value="personal">Personal</TabsTrigger>
                <TabsTrigger value="experience">Experience</TabsTrigger>
                <TabsTrigger value="education">Education</TabsTrigger>
                <TabsTrigger value="skills">Skills</TabsTrigger>
              </TabsList>

              <TabsContent value="personal" className="space-y-4 mt-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Personal Information</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label>Full Name</Label>
                        <Input 
                          value={resume.full_name} 
                          onChange={(e) => setResume(prev => ({ ...prev, full_name: e.target.value }))}
                          data-testid="input-fullname"
                        />
                      </div>
                      <div>
                        <Label>Email</Label>
                        <Input 
                          value={resume.email} 
                          onChange={(e) => setResume(prev => ({ ...prev, email: e.target.value }))}
                          data-testid="input-email"
                        />
                      </div>
                      <div>
                        <Label>Phone</Label>
                        <Input 
                          value={resume.phone} 
                          onChange={(e) => setResume(prev => ({ ...prev, phone: e.target.value }))}
                          data-testid="input-phone"
                        />
                      </div>
                      <div>
                        <Label>LinkedIn</Label>
                        <Input 
                          value={resume.linkedin} 
                          onChange={(e) => setResume(prev => ({ ...prev, linkedin: e.target.value }))}
                          placeholder="linkedin.com/in/yourprofile"
                          data-testid="input-linkedin"
                        />
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <Label>Professional Summary</Label>
                        <Button variant="ghost" size="sm" onClick={() => getAISuggestions('summary')} data-testid="ai-summary-btn">
                          <Sparkles size={14} className="mr-1 text-purple-500" /> AI Suggest
                        </Button>
                      </div>
                      <Textarea 
                        value={resume.summary} 
                        onChange={(e) => setResume(prev => ({ ...prev, summary: e.target.value }))}
                        rows={4}
                        placeholder="Write a compelling summary of your professional background..."
                        data-testid="input-summary"
                      />
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="experience" className="space-y-4 mt-4">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Briefcase size={20} /> Work Experience
                    </CardTitle>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" onClick={() => getAISuggestions('experience')} data-testid="ai-experience-btn">
                        <Sparkles size={14} className="mr-1 text-purple-500" /> AI Suggest
                      </Button>
                      <Button variant="outline" size="sm" onClick={addExperience} data-testid="add-experience-btn">
                        <Plus size={14} className="mr-1" /> Add
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {resume.experience.length === 0 ? (
                      <p className="text-center text-muted-foreground py-8">No experience added yet. Click Add to get started.</p>
                    ) : (
                      resume.experience.map((exp, index) => (
                        <div key={index} className="border rounded-lg p-4 space-y-3">
                          <div className="flex justify-between">
                            <h4 className="font-medium">Position {index + 1}</h4>
                            <Button variant="ghost" size="sm" onClick={() => removeExperience(index)}>
                              <Trash2 size={14} className="text-red-500" />
                            </Button>
                          </div>
                          <div className="grid md:grid-cols-2 gap-3">
                            <Input placeholder="Company" value={exp.company} onChange={(e) => updateExperience(index, 'company', e.target.value)} />
                            <Input placeholder="Job Title" value={exp.title} onChange={(e) => updateExperience(index, 'title', e.target.value)} />
                            <Input placeholder="Dates (e.g., Jan 2020 - Present)" value={exp.dates} onChange={(e) => updateExperience(index, 'dates', e.target.value)} />
                          </div>
                          <Textarea placeholder="Description of responsibilities and achievements..." value={exp.description} onChange={(e) => updateExperience(index, 'description', e.target.value)} rows={3} />
                        </div>
                      ))
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="education" className="space-y-4 mt-4">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <GraduationCap size={20} /> Education
                    </CardTitle>
                    <Button variant="outline" size="sm" onClick={addEducation} data-testid="add-education-btn">
                      <Plus size={14} className="mr-1" /> Add
                    </Button>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {resume.education.length === 0 ? (
                      <p className="text-center text-muted-foreground py-8">No education added yet.</p>
                    ) : (
                      resume.education.map((edu, index) => (
                        <div key={index} className="border rounded-lg p-4 space-y-3">
                          <div className="flex justify-between">
                            <h4 className="font-medium">Education {index + 1}</h4>
                            <Button variant="ghost" size="sm" onClick={() => removeEducation(index)}>
                              <Trash2 size={14} className="text-red-500" />
                            </Button>
                          </div>
                          <div className="grid md:grid-cols-2 gap-3">
                            <Input placeholder="School/University" value={edu.school} onChange={(e) => updateEducation(index, 'school', e.target.value)} />
                            <Input placeholder="Degree" value={edu.degree} onChange={(e) => updateEducation(index, 'degree', e.target.value)} />
                            <Input placeholder="Dates" value={edu.dates} onChange={(e) => updateEducation(index, 'dates', e.target.value)} />
                          </div>
                        </div>
                      ))
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="skills" className="space-y-4 mt-4">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Award size={20} /> Skills
                    </CardTitle>
                    <Button variant="ghost" size="sm" onClick={() => getAISuggestions('skills')} data-testid="ai-skills-btn">
                      <Sparkles size={14} className="mr-1 text-purple-500" /> AI Suggest
                    </Button>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {resume.skills.map((skill, index) => (
                        <Badge key={index} variant="secondary" className="px-3 py-1">
                          {skill}
                          <button onClick={() => removeSkill(skill)} className="ml-2 text-red-500 hover:text-red-700">×</button>
                        </Badge>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <Input 
                        placeholder="Add a skill..." 
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            addSkill(e.target.value);
                            e.target.value = '';
                          }
                        }}
                        data-testid="input-skill"
                      />
                      <Button variant="outline" onClick={(e) => {
                        const input = e.target.previousSibling;
                        addSkill(input.value);
                        input.value = '';
                      }}>Add</Button>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>

          {/* Template Selection */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Choose Template</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {Object.entries(templates).map(([key, template]) => (
                  <div 
                    key={key}
                    className={`border rounded-lg p-3 cursor-pointer transition-all ${selectedTemplate === key ? 'border-blue-500 bg-blue-50' : 'hover:border-gray-400'}`}
                    onClick={() => setSelectedTemplate(key)}
                    data-testid={`template-${key}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-4 h-12 rounded ${TEMPLATE_COLORS[key]}`}></div>
                      <div>
                        <h4 className="font-medium">{template.name}</h4>
                        <p className="text-xs text-muted-foreground">{template.description}</p>
                      </div>
                      {selectedTemplate === key && <CheckCircle size={16} className="text-blue-500 ml-auto" />}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Preview */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Preview</CardTitle>
              </CardHeader>
              <CardContent>
                <div className={`border rounded-lg p-4 text-xs ${TEMPLATE_COLORS[selectedTemplate]} bg-opacity-5`}>
                  <div className={`h-2 w-full ${TEMPLATE_COLORS[selectedTemplate]} rounded mb-3`}></div>
                  <h3 className="font-bold text-sm mb-1">{resume.full_name || 'Your Name'}</h3>
                  <p className="text-muted-foreground mb-2">{resume.email}</p>
                  <div className="space-y-2">
                    <div className="h-2 bg-gray-200 rounded w-full"></div>
                    <div className="h-2 bg-gray-200 rounded w-3/4"></div>
                    <div className="h-2 bg-gray-200 rounded w-5/6"></div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* AI Suggestions Dialog */}
      <Dialog open={showAISuggestions} onOpenChange={setShowAISuggestions}>
        <DialogContent className="max-w-lg" data-testid="ai-suggestions-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="text-purple-500" /> AI Suggestions
            </DialogTitle>
          </DialogHeader>
          {aiSuggestions && (
            <div className="space-y-4">
              <div className="space-y-2">
                {aiSuggestions.suggestions.map((suggestion, index) => (
                  <div key={index} className="border rounded-lg p-3 hover:bg-gray-50">
                    <p className="text-sm mb-2">{suggestion}</p>
                    <Button size="sm" variant="outline" onClick={() => applySuggestion(suggestion)}>
                      Apply
                    </Button>
                  </div>
                ))}
              </div>
              {aiSuggestions.tips && (
                <div className="bg-purple-50 rounded-lg p-3">
                  <h4 className="font-medium text-sm mb-2">💡 Tips</h4>
                  <ul className="text-xs space-y-1 text-muted-foreground">
                    {aiSuggestions.tips.map((tip, index) => (
                      <li key={index}>• {tip}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}
